import { useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import Icon from '../../common/Icon/Icon';
import { IconKeys } from '../../common/Icon/IconKeys';
import Modal from '../../common/Modal/Modal';
import DeleteModal from '../../common/DeleteModal/DeleteModal';
import S from './NoteItemStyles';
import EditNote from '../EditNote/EditNote';
import useDeleteNote from '../../../API/hooks/notes/useDeleteNote';
import { logger } from '../../../API/services/SharedService';
import useGetRole from '../../../API/hooks/useGetRole';

type Props = {
  item?: {
    _id: string,
    createdAt: string;
    updatedAt?: string;
    authorId?: string;
    entityName: 'alerts' | 'drivers' | 'vehicles' | 'devices' | 'trips' | 'videos';
    entityId: string;
    fleetId: string;
    title: string;
    text: string;
    isAutoGenerated?: boolean;
  },
  lightVersion?: boolean,
};

function NoteItem({ item, lightVersion }: Props): JSX.Element {
  const [editNoteModalOpen, toggleEditNoteModalOpen] = useState(false);
  const [deleteNoteModalOpen, toggleDeleteNoteModalOpen] = useState(false);
  const { user: { username, attributes } } = useAuthenticator();
  const {
    role, isAdmin, isSupport, isFleetAdmin,
  } = useGetRole();

  const closeModal = (): void => {
    toggleDeleteNoteModalOpen(false);
  };

  const onDeleteSubmitFail = (err: string): void => {
    logger.error('Delete note error', err);
  };

  const getFormattedTime = (evDate: string): string => {
    const d = (new Date(evDate)).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
    });
    const t = (new Date(evDate)).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const y = (new Date(evDate)).getFullYear() !== (new Date()).getFullYear() ? `, ${(new Date(evDate)).getFullYear()}` : '';
    return `${d}${y} at ${t}`;
  };

  const { mutate: mutateDelete, isLoading: isDeleteLoading } = useDeleteNote(
    closeModal,
    onDeleteSubmitFail,
  );

  if (!item) {
    return (<p>no data</p>);
  }

  const handleDeleteNote = (): void => mutateDelete({
    fleetId: item.fleetId,
    id: item._id,
  });

  const onClickLink = (): void => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const copyContent = async () => {
      try {
        await navigator.clipboard.writeText(`${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/notes/${item._id}`);
        logger.info('Note', 'URL copied to clipboard');
      } catch (err) {
        logger.error('Note', 'Failed to copy: ', err);
      }
    };
    copyContent();
  };

  const onClickEdit = (): void => {
    toggleEditNoteModalOpen(true);
  };

  const onClickDelete = (): void => {
    toggleDeleteNoteModalOpen(true);
  };

  return (
    <S.Wrapper>
      <div className="header">
        <h3 className="header-title">{item.title}</h3>
        {item.isAutoGenerated && <span className="auto-generated-badge">Auto-generated</span>}
      </div>
      <div className="notes-card-container">
        {item.text}
      </div>
      <div className="notes-card-footer">
        <div className="info">
          <p className="type">{item.entityName}</p>
          <p>{getFormattedTime(item.createdAt)}{item.updatedAt && item.createdAt !== item.updatedAt ? `  (Last updated: ${getFormattedTime(item.updatedAt)})` : ''}</p>
        </div>
        <div className="actions">
          {/* TODO enable link button after implementation of Note page */}
          {/* <button className="note-link" onClick={onClickLink} type="button" title="Get link"><Icon iconKey={IconKeys.LINK} /> </button> */}
          {!item.isAutoGenerated && (username === item.authorId || isAdmin || isSupport || isFleetAdmin) && (
            <button className="note-edit" onClick={onClickEdit} type="button" title="Edit"><Icon iconKey={IconKeys.EDIT_NOTE} /> </button>
          )}
          {!item.isAutoGenerated && (username === item.authorId || isAdmin || isSupport || isFleetAdmin) && (
            <button className="note-delete" onClick={onClickDelete} type="button" title="Delete"><Icon iconKey={IconKeys.DELETE} /> </button>
          )}
        </div>
      </div>
      {editNoteModalOpen && (
        <Modal
          title="Edit note"
          state={editNoteModalOpen}
          toggleState={toggleEditNoteModalOpen}
        >
          <EditNote
            noteId={item._id}
            fleetId={item.fleetId}
            entityName={item.entityName}
            entityId={item.entityId}
            toggleModal={toggleEditNoteModalOpen}
            title={item.title}
            text={item.text}
          />
        </Modal>
      )}
      {deleteNoteModalOpen && (
        <Modal
          title="Confirm delete"
          state={deleteNoteModalOpen}
          toggleState={toggleDeleteNoteModalOpen}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete this note?"
            onClose={() => toggleDeleteNoteModalOpen(false)}
            onDelete={handleDeleteNote}
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default NoteItem;
